import _ from 'lodash';
import {
  sqDurationStore,
  sqItemRuntimeStore,
  sqSeriesRuntimeStore,
  sqTrendSeriesStore,
  sqTrendStore,
} from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { getCapsuleFormula } from '@/datetime/dateTime.utilities';
import { getAlignableItems, getTrendStores } from '@/trend/trendDataHelper.utilities';
import { recordDataStatus } from '@/trendData/itemRuntime.utilities';
import { createStringEnum, extractSamplesForCapsule } from '@/trendData/seriesData.utilities';
import { getSeriesDataConfig, getSeriesDataContext } from '@/trendData/seriesRuntime.actions';
import {
  createFormulaCondition,
  fetchItemProps,
  fetchTimeseries,
  setCustomizationProps,
  setTrendItemProps,
} from '@/trendData/trend.actions';
import { ITEM_DATA_STATUS, SAMPLE_OPTIONS } from '@/trendData/trendData.constants';
import { getLaneForReservation, reindexOrderInLaneForReservation } from '@/trendData/trendLaneReservation.utilities';
import { getCapsuleSeries } from '@/trendData/trendSeries.selectors';
import { getPreviewSeriesId } from '@/trendData/trendSeries.utilities';
import { addSignal } from '@/utilities/autoGroup.utilities';
import { computeTable } from '@/utilities/formula.utilities';
import { getMSPerPixelWidth } from '@/utilities/utilities';
import { SeeqNames } from '@seeqdev/constants';
import type { PushOption } from '@/core/flux.service';
import type { RangeExport } from '@/trendData/duration.store';
import type { DataStatusPayload } from '@/trendData/itemStoreBase.types';
import type { BaseItem, SeriesItem } from '@/trendData/trendData.types';
import type { AddDataPayload, PreviewSeriesPayload, ShadedAreaBound } from '@/trendData/trendSeries.store';
import type { Capsule } from '@/utilities/datetime.constants';

export function setSeriesDataStatusLoading(id: string): void {
  recordDataStatus(ITEM_DATA_STATUS.LOADING, { id });
}

export function setSeriesDataStatusPresent(payload: DataStatusPayload): void {
  recordDataStatus(ITEM_DATA_STATUS.PRESENT, payload);
}

export function setSeriesDataStatusNotRequired(id: string, ...option: [PushOption?]): void {
  recordDataStatus(ITEM_DATA_STATUS.NOT_REQUIRED, { id });
  sqSeriesRuntimeStore.actions.clearData(id);
  flux.dispatch('TREND_SET_DATA_STATUS_NOT_REQUIRED', { id }, ...option);
}

export function setSeriesDataStatusHiddenFromTrend(id: string, ...option: [PushOption?]): void {
  recordDataStatus(ITEM_DATA_STATUS.HIDDEN_FROM_TREND, { id });
  sqSeriesRuntimeStore.actions.clearData(id);
  flux.dispatch('TREND_SET_DATA_STATUS_HIDDEN_FROM_TREND', { id }, ...option);
}

/**
 * Records the results of a series data fetch: derives the render data into the series runtime
 * store and dispatches the results.
 */
export function recordSeriesData(payload: AddDataPayload, ...option: [PushOption?]): void {
  recordSeriesRenderData(payload.id, payload);
  flux.dispatch('TREND_SERIES_RESULTS_SUCCESS', payload, ...option);
}

/**
 * Records the results of a fetch covering many capsules at once. The samples of each capsule series
 * are cut from the single set of samples the fetch returned.
 */
export function recordSignalSegmentsData(
  payload: AddDataPayload & {
    /** `findCapsulesQueryInterval` tags each interval with the item and capsule it was built for */
    requestCapsules: Array<Capsule & { interestId?: string; capsuleId?: string }>;
  },
  ...option: [PushOption?]
): void {
  const stringEnum = payload.stringEnum ?? createStringEnum(payload.samples);
  recordSeriesRenderData(payload.id, { ...payload, stringEnum });

  // Resolve each interval's capsule series here so the flux handler needs no capsule-store view
  const capsuleSeries = getCapsuleSeries();
  const requestCapsules = payload.requestCapsules.map((requestCapsule) => ({
    ...requestCapsule,
    capsuleSeriesId: capsuleSeries.find(
      (series) => series.interestId === requestCapsule.interestId && series.capsuleId === requestCapsule.capsuleId,
    )?.id,
  }));

  requestCapsules.forEach(({ capsuleSeriesId, startTime, endTime }) => {
    if (capsuleSeriesId) {
      recordSeriesRenderData(capsuleSeriesId, {
        ...payload,
        stringEnum,
        samples: extractSamplesForCapsule(payload.samples, startTime, endTime),
      });
    }
  });

  flux.dispatch('TREND_MULTIPLE_SERIES_DATA_RESULTS_SUCCESS', { ...payload, requestCapsules }, ...option);
}

/**
 * Derives and records the render data of one series. The configuration comes from the item that owns
 * the data, so an id with no item is skipped.
 */
function recordSeriesRenderData(id: string, payload: Omit<AddDataPayload, 'id'>): void {
  const item = sqTrendSeriesStore.findItem(id);
  if (!item) {
    return;
  }

  sqSeriesRuntimeStore.actions.setData(id, {
    payload: {
      samples: payload.samples,
      valueUnitOfMeasure: payload.valueUnitOfMeasure ?? '',
      interpolationMethod: payload.interpolationMethod,
      stringEnum: payload.stringEnum,
      capsuleSegmentSamples: payload.capsuleSegmentSamples,
    },
    config: getSeriesDataConfig(item),
    context: getSeriesDataContext(),
  });
}

/**
 * Empties a series' drawable data without touching its config.
 */
export function clearSeriesData(id: string): void {
  sqSeriesRuntimeStore.actions.clearData(id);
}

/**
 * Removes the capsule series matching the given capsule/interest pairs, discarding their runtime
 * state so nothing stays keyed by ids the store no longer knows.
 */
export function removeSeriesFromCapsules(
  payload: { seriesFromCapsules: Array<{ capsuleId?: string; interestId?: string }> },
  ...option: [PushOption?]
): void {
  // Resolve the matched series here so the flux handler needs no capsule-store view
  const capsuleSeriesIds = getCapsuleSeries()
    .filter((series) =>
      payload.seriesFromCapsules.some(
        ({ capsuleId, interestId }) => series.capsuleId === capsuleId && series.interestId === interestId,
      ),
    )
    .map(({ id }) => id);
  capsuleSeriesIds.forEach((id) => {
    sqSeriesRuntimeStore.actions.clearValues(id);
    sqItemRuntimeStore.actions.clearItem(id);
  });
  flux.dispatch('TREND_REMOVE_SERIES_FROM_CAPSULE', { ...payload, capsuleSeriesIds }, ...option);
}

/** Removes every capsule series, discarding their runtime state with them. */
export function removeAllCapsuleSeries(): void {
  const capsuleSeriesIds = getCapsuleSeries().map(({ id }) => id);
  capsuleSeriesIds.forEach((id) => {
    sqSeriesRuntimeStore.actions.clearValues(id);
    sqItemRuntimeStore.actions.clearItem(id);
  });
  flux.dispatch('TREND_REMOVE_ALL_CAPSULE_SERIES', { capsuleSeriesIds });
}

/**
 * Records the preview of a series being built by a tool. An empty result removes the preview.
 */
export function recordPreviewSeries(payload: Partial<PreviewSeriesPayload>): void {
  clearPreviewSeriesData();

  if (payload.samples?.length) {
    const previewId = getPreviewSeriesId(payload.id);
    sqSeriesRuntimeStore.actions.setData(previewId, {
      payload: {
        samples: payload.samples,
        valueUnitOfMeasure: payload.valueUnitOfMeasure ?? '',
        interpolationMethod: payload.interpolationMethod,
        stringEnum: payload.stringEnum,
        capsuleSegmentSamples: payload.capsuleSegmentSamples,
      },
      config: {
        color: payload.color ?? undefined,
        lineWidth: payload.lineWidth,
        minTime: undefined,
        hasBounds: false,
        sampleDisplayOption: payload.sampleDisplayOption ?? SAMPLE_OPTIONS.LINE,
      },
      context: getSeriesDataContext(),
    });
  }

  flux.dispatch('TREND_SET_CHART_SERIES_PREVIEW', payload);
}

/**
 * Removes the series preview.
 */
export function clearPreviewSeries(): void {
  clearPreviewSeriesData();
  flux.dispatch('TREND_REMOVE_CHART_SERIES_PREVIEW');
}

/** Discards the render data of whatever preview is currently displayed. */
function clearPreviewSeriesData(): void {
  const preview = sqTrendSeriesStore.previewChartItem;
  if (!preview) {
    return;
  }
  const previews = Array.isArray(preview) ? preview : [preview];
  previews.forEach((item) => sqSeriesRuntimeStore.actions.clearValues(item.id));
}

export function setSeriesProperties(payload: { id: string } & Record<string, unknown>, ...option: [PushOption?]): void {
  flux.dispatch('TREND_SET_PROPERTIES', payload, ...option);
}

export function setEditModeForSeries(seriesId: string | number | undefined) {
  flux.dispatch('TREND_SET_EDITING_SERIES_ID', { id: seriesId });
}

export function addSeries(
  item: SeriesItem & {
    properties?: Array<{ name: string; value: any }>;
  },
  props?: object,
  option?: PushOption,
) {
  if (!sqTrendSeriesStore.findItem(item.id)) {
    const reservation = item.laneReservation
      ? getLaneForReservation(
          item.laneReservation,
          item.laneReservationOptions,
          setCustomizationProps,
          () => getAlignableItems({}) as BaseItem[],
        )
      : undefined;

    const payload = {
      id: item.id,
      interpolationMethod: _.find(item?.properties, {
        name: SeeqNames.Properties.InterpolationMethod,
      }),
      name: item.name,
      isArchived: !!item.isArchived,
      lane: reservation?.lane ?? (item.lane ? item.lane : sqTrendStore.getNextLane()),
      alignment: reservation?.axisAlign ?? (item.axisAlign ? item.axisAlign : sqTrendStore.nextAlignment),
      color: item.color,
      effectivePermissions: item.effectivePermissions,
      laneReservation: item.laneReservation,
    };

    flux.dispatch('TREND_ADD_SERIES', payload, option);

    if (item.laneReservation) {
      reindexOrderInLaneForReservation(
        item.laneReservation,
        setCustomizationProps,
        () => getAlignableItems({}) as BaseItem[],
      );
    }
  }

  setTrendItemProps(item.id, props, option);

  return Promise.all([
    fetchItemProps(item.id).then(() => {
      addSignal({
        id: item.id,
        trendStores: getTrendStores(),
      });
    }),
    fetchTimeseries(item.id),
  ]);
}

/**
 * Fetch signal data for a pair of signals.
 *
 * @param shadedAreaLower - Lower bound item
 * @param shadedAreaUpper - Upper bound item
 * @param range - Display range
 * @param numPixels - the number or horizontal pixels available for signal display
 * @param cancellationGroup - the group used to cancel the requests
 * @returns a promise that resolves with the aligned signal data
 */
export function fetchPairedSignal(
  shadedAreaLower: ShadedAreaBound,
  shadedAreaUpper: ShadedAreaBound,
  range: RangeExport,
  numPixels: number,
  cancellationGroup: string,
) {
  const laneWidth = `${getMSPerPixelWidth(range.duration, numPixels)}ms`;
  return fetchSampleTableForSignalPair(
    `sampleTable(${[
      getCapsuleFormula({ start: range.start, end: range.end }),
      laneWidth,
      `$lower${!shadedAreaLower.isSignal ? '.toSignal()' : ''}`,
      `$upper${!shadedAreaUpper.isSignal ? '.toSignal()' : ''}`,
    ].join(', ')})`,
    shadedAreaLower.id,
    shadedAreaUpper.id,
    cancellationGroup,
  );
}

/**
 * Fetch signal data for a pair of signals within a condition.
 *
 * @param parentItemId - id of the parent condition
 * @param shadedAreaLower - Lower bound item
 * @param shadedAreaUpper - Upper bound item
 * @param range - Display range
 * @param numPixels - the number or horizontal pixels available for signal display
 * @param cancellationGroup - the group used to cancel the requests
 * @returns a promise that resolves with the aligned signal data
 */
export function fetchPairedSignalWithinCondition(
  parentItemId: string,
  shadedAreaLower: ShadedAreaBound,
  shadedAreaUpper: ShadedAreaBound,
  range: RangeExport,
  numPixels: number,
  cancellationGroup: string,
) {
  const laneWidth = `${getMSPerPixelWidth(range.duration, numPixels)}ms`;
  return fetchSampleTableForSignalPair(
    [
      `$condition = ${createFormulaCondition(parentItemId)}`,
      `sampleTable(${[
        getCapsuleFormula(sqDurationStore.displayRange),
        laneWidth,
        `$lower${!shadedAreaLower.isSignal ? '.toSignal()' : ''}.within($condition)`,
        `$upper${!shadedAreaUpper.isSignal ? '.toSignal()' : ''}.within($condition)`,
      ].join(', ')})`,
    ].join('\n'),
    shadedAreaLower.id,
    shadedAreaUpper.id,
    cancellationGroup,
  );
}

/**
 * Fetch a paired signal and return a promise with the results, in the same format as fetchOneSignal. This is needed
 * for shaded areas on the chart which are produced from two signals (boundaries in Metrics), but which Highcharts
 * treats as one series. In order to get a value for both the upper and lower signal the keys of both signals must
 * be aligned which is why the sampleTable operator is used. By default it will align the keys based on the sample
 * keys, but that can leave gaps at the edges since it does not include  the boundary values (CRAB-44921). To work
 * around this the $period parameter is set to the amount of time each pixel represents. This can result in it
 * returning more data than needed since it will always return at least one sample per pixel, but that is also what
 * spikecatcher can do for dense data and it solves the problem for these paired signals.
 */
async function fetchSampleTableForSignalPair(
  formula: string,
  lowerId: string,
  upperId: string,
  cancellationGroup: string,
) {
  const results = await computeTable({
    usePost: true, // Needed in case the formula is long
    formula,
    parameters: { lower: lowerId, upper: upperId },
    cancellationGroup,
  });

  return {
    valueUnitOfMeasure: results.headers[1].units,
    samples: _.map(results.data, ([key, lower, upper]) => ({
      key,
      lower,
      upper,
    })),
    timingInformation: results.timingInformation,
    meterInformation: results.meterInformation,
    warningLogs: results.warningLogs,
    warningCount: results.warningCount,
  };
}
